import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Tooltip,
  Image,
  Upload,
  Space,
} from "antd";
import { fetchTest } from "../test/FetchTest";
import { useSelector } from "react-redux";
import { fetchPackage } from "./FetchPackage";
import { fetchLab } from "../labPartner/FetchLab";
import { EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import BulkUploadPackage from "./BulkUploadPackage";
import { fetchTAT } from "../tat/FetchTAT";
import { fetchTestRequirement } from "../testRequirements/FetchTestRequirement";
import { fetchTestTag } from "../testTags/FetchTestTags";
import { fetchTestCategory } from "../testCategory/FetchTestCategory";
import Highlighter from "react-highlight-words";
import { token } from "../../constants/Constants";
import { createPackage, get_all_tests_labs, updatePackage } from "../../endpoints/API";

const { TextArea } = Input;
const { Option } = Select;

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const Package = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [lab, setLab] = useState([]);
  const [test, setTest] = useState([]);
  const [isPackage, setPackage] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [TAT, setTAT] = useState([]);
  const [TestRequirements, setTestRequirements] = useState([]);
  const [testTags, setTestTags] = useState([]);
  const [testCategory, setTestCategory] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedLabId, setSelectedLabId] = useState(null);
  const searchInput = useRef(null);
  const isUser = useSelector((state) => state.user.user);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0] || ""}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedKeys(value ? [value] : []);
          }}
          onPressEnter={() => {
            confirm();
            setSearchText(selectedKeys[0] || "");
            setSearchedColumn(dataIndex);
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => {
              confirm();
              setSearchText(selectedKeys[0] || "");
              setSearchedColumn(dataIndex);
            }}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              setSearchText("");
              setSearchedColumn("");
              confirm({ closeDropdown: false });
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Test ID",
      dataIndex: "test_id",
      key: "test_id",
      ...getColumnSearchProps("test_id"),
    },
    {
      title: "Package Name",
      dataIndex: "name_of_package",
      key: "name_of_package",
      ...getColumnSearchProps("name_of_package"),
    },
    {
      title: "Lab Name",
      dataIndex: "lab_name",
      key: "lab_name",
      ...getColumnSearchProps("lab_name"),
    },
    {
      title: "TAT",
      dataIndex: "tat_name",
      key: "tat_name",
      ...getColumnSearchProps("tat_name"),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description) => (
        <Tooltip title={description || "N/A"}>
          <div
            style={{
              width: "5rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {description || "N/A"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "B2C Price",
      dataIndex: "b2c_price",
      key: "b2c_price",
      render: (b2c_price) => (b2c_price ? `₹${b2c_price}` : "N/A"),
    },
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      render: (mrp) => (mrp ? `₹${mrp}` : "N/A"),
    },
    {
      title: "B2B Price",
      dataIndex: "b2b_price",
      key: "b2b_price",
      render: (b2b_price) => (b2b_price ? `₹${b2b_price}` : "N/A"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            setEditingRecord(record);
            setIsModalOpen(true);
            setSelectedLabId(record.lab_id);
            form.setFieldsValue({
              name: record.name_of_package,
              lab: record.lab_id,
              test: record.tests?.map((testObj) => testObj?.id),
              b2cPrice: record.b2c_price,
              mrp: record.mrp,
              b2bPrice: record.b2b_price,
              description: record.description,
              testId: record.test_id,
              tat: record.tat,
              testRequirements: record.test_requirements?.map(
                (testReq) => testReq?.id
              ),
              tags: record.tags?.map((tag) => tag?.id),
              priority: record.priority,
              testCategory: record.test_category?.map(
                (testCategory) => testCategory?.id
              ),
              also_known_as: record.also_known_as,
              desc: record.also_known_as,
            });
          }}
        >
          <EditOutlined />
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const getPackageDetails = async () => {
      try {
        await fetchLab(setLab, baseUrl, prodTestUrl);
        await fetchTest(setTest, baseUrl, prodTestUrl);
        await fetchPackage(setPackage, baseUrl, prodTestUrl);
        await fetchTAT(setTAT, baseUrl, prodTestUrl);
        await fetchTestRequirement(setTestRequirements, baseUrl, prodTestUrl);
        await fetchTestTag(setTestTags, baseUrl, prodTestUrl);
        await fetchTestCategory(setTestCategory, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error || "Failed to fetch lab.");
      }
    };
    getPackageDetails();
  }, []);

  useEffect(() => {
    if (!selectedLabId) return;
    const fetchTestsForLab = async () => {
      try {
        const response = await get_all_tests_labs(selectedLabId);
        const data = response?.data;
        if (response.statusText == "OK") {
          setTest(data);
        } else {
          message.error("No tests for the selected lab.");
          setTest(null);
        }
      } catch (error) {
        message.error(
          error?.response?.data?.error ||
            "Failed to fetch tests for the selected lab."
        );
      }
    };
    fetchTestsForLab();
  }, [selectedLabId]);

  const onFormValuesChange = (changedValues, allValues) => {
    if (changedValues.lab) {
      setSelectedLabId(changedValues.lab);
    }
  };

  const handleCreatePackage = async (values) => {
    try {
      const base64Files = await Promise.all(
        fileList
          .filter((file) => file.originFileObj)
          .map((file) => getBase64(file.originFileObj))
      );

      const payload = {
        icon: base64Files[0],
        testId: values.testId,
        name_of_package: values.name,
        tat: values.tat,
        test_requirements: values.testRequirements,
        lab_id: values.lab,
        test: values.test,
        b2c_price: values.b2cPrice,
        b2b_price: values.b2bPrice,
        mrp: values.mrp,
        tags: values.tags,
        priority: values.priority,
        testCategory: values.testCategory,
        also_known_as: values.also_known_as,
        description: values.description,
        created_by: isUser.user_id,
      };

      let response;
      if (editingRecord) {
        response = await updatePackage(editingRecord.id, payload);
      } else {
        response = await createPackage(payload);
      }

      if (response.statusText == "OK") {
        message.success(
          editingRecord
            ? "Package updated successfully!"
            : "Package created successfully!"
        );
        await fetchPackage(setPackage, baseUrl, prodTestUrl);
        setIsModalOpen(false);
        form.resetFields();
        setEditingRecord(null);
      } else {
        const errorData = response?.data;
        message.error(errorData.error || "Failed to save package.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.error || "An error occurred. Please try again."
      );
    }
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files.");
    }
    return isImage;
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    try {
      const base64 = await getBase64(file);
      onSuccess("ok");
    } catch (error) {
      onError(error);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setEditingRecord(null);
  };

  const handleBulkUploadCancel = () => {
    setIsBulkUploadModalOpen(false);
  };

  return (
    <div>
      {/* Header row — wraps on mobile */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <Button type="primary" onClick={() => setIsBulkUploadModalOpen(true)}>
          Bulk Upload
        </Button>
        <Button
          type="primary"
          onClick={() => {
            setEditingRecord(null);
            setIsModalOpen(true);
            form.resetFields();
          }}
        >
          Create Package
        </Button>
      </div>

      {/* Scrollable table wrapper */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          dataSource={isPackage.packages}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Create / Edit Package Modal */}
      <Modal
        title={editingRecord ? "Edit Package" : "Create Package"}
        visible={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width="min(600px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreatePackage}
          onValuesChange={onFormValuesChange}
        >
          <Form.Item label="Image" layout="vertical" name="image">
            <Upload
              customRequest={handleCustomRequest}
              listType="picture-circle"
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              onPreview={handlePreview}
              maxCount={1}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>

          <Form.Item
            label="Test ID"
            name="testId"
            layout="vertical"
            rules={[{ required: true, message: "Please enter Test ID!" }]}
          >
            <Input placeholder="Enter Test ID" />
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter package name!" }]}
          >
            <Input placeholder="Enter package name" />
          </Form.Item>

          <Form.Item
            label="TAT"
            name="tat"
            layout="vertical"
            rules={[{ required: true, message: "Please select TAT!" }]}
          >
            <Select
              placeholder="Select TAT"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {TAT?.map((tat) => (
                <Select.Option key={tat.id} value={tat.id}>
                  {tat.tat_pickup_time + " " + tat.duration_unit}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Test Requirements"
            name="testRequirements"
            layout="vertical"
            rules={[
              { required: true, message: "Please select Test Requirements!" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select Test Requirements"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {TestRequirements?.map((req) => (
                <Select.Option key={req.id} value={req.id}>
                  {req.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Lab"
            name="lab"
            rules={[{ required: true, message: "Please select Lab!" }]}
          >
            <Select
              placeholder="Select lab"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {lab?.map((labItem) => (
                <Select.Option key={labItem.id} value={labItem.id}>
                  {labItem.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Test"
            name="test"
            rules={[{ required: true, message: "Please select tests!" }]}
          >
            <Select
              placeholder="Select test"
              mode="multiple"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={!selectedLabId}
            >
              {(test ?? []).map((t) => (
                <Option key={t.id} value={t.id}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="B2C Price"
            name="b2cPrice"
            rules={[{ required: true, message: "Please enter B2C Price!" }]}
          >
            <Input placeholder="Enter B2C Price" />
          </Form.Item>

          <Form.Item
            label="MRP"
            name="mrp"
            rules={[{ required: true, message: "Please enter MRP!" }]}
          >
            <Input placeholder="Enter MRP" />
          </Form.Item>

          <Form.Item
            label="B2B Price"
            name="b2bPrice"
            rules={[{ required: true, message: "Please enter B2B Price!" }]}
          >
            <Input placeholder="Enter B2B Price" />
          </Form.Item>

          <Form.Item
            label="Test Tags"
            name="tags"
            rules={[{ required: true, message: "Please select Tags!" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select Tags"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {testTags?.map((tag) => (
                <Select.Option key={tag.id} value={tag.id}>
                  {tag.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Priority"
            name="priority"
            rules={[{ required: true, message: "Please enter Priority!" }]}
          >
            <Input placeholder="Enter Priority" />
          </Form.Item>

          <Form.Item
            label="Test Category"
            name="testCategory"
            rules={[{ required: true, message: "Please select a Category!" }]}
          >
            <Select
              placeholder="Select Category"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              mode="multiple"
            >
              {testCategory?.data?.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Also Known As"
            name="also_known_as"
            rules={[
              { required: true, message: "Please enter a also known as!" },
            ]}
          >
            <Input.TextArea rows={4} placeholder="Enter also known as" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter a description!" }]}
          >
            <TextArea rows={4} placeholder="Enter description" />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button type="primary" htmlType="submit">
                {editingRecord ? "Update" : "Submit"}
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        title="Bulk Upload Packages"
        visible={isBulkUploadModalOpen}
        onCancel={handleBulkUploadCancel}
        footer={null}
        width="min(800px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <BulkUploadPackage
          setPackage={setPackage}
          Lab={lab}
          setIsBulkUploadModalOpen={setIsBulkUploadModalOpen}
          baseUrl={baseUrl}
          prodTestUrl={prodTestUrl}
        />
      </Modal>
    </div>
  );
};

export default Package;